import {
  Button,
  Alert,
  PhoneInputField,
  FormField,
  TextInput,
  SuccessNotice,
} from "@a2z/ui";

import { useEffect, useMemo, useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { toE164OrNull } from "@/lib/phone/phone";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useUpdateSellerMutation } from "../services/useUpdateSellerMutation";
import { useLocale } from "@admin/app/locale";
import { getPhoneInputLabels } from "@/shared/components/forms/phone-input-labels";

export function SellerEditForm({
  sellerId,
  defaultValues,
}: {
  sellerId: string;
  defaultValues: { name?: string; email?: string; phone?: string };
}) {
  const t = useTranslations();
  const locale = useLocale();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [phone, setPhone] = useState(defaultValues.phone ?? "");
  const { mutateAsync: updateSeller } = useUpdateSellerMutation();
  const formKey = useMemo(
    () =>
      `${defaultValues.name ?? ""}:${defaultValues.email ?? ""}:${defaultValues.phone ?? ""}`,
    [defaultValues.email, defaultValues.name, defaultValues.phone],
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- keep controlled phone input synced when default values load asynchronously
    setPhone(defaultValues.phone ?? "");
  }, [defaultValues.phone]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSuccess(false);
    const formData = new FormData(event.currentTarget);
    const normalizedPhone =
      phone.trim().length === 0
        ? undefined
        : (toE164OrNull(phone) ?? undefined);
    if (phone.trim().length > 0 && !normalizedPhone) {
      setError(t(keys.buyer.trackingValidationPhone));
      return;
    }

    try {
      await updateSeller({
        sellerId,
        name: String(formData.get("name") ?? ""),
        email: String(formData.get("email") ?? ""),
        phone: normalizedPhone,
      });
    } catch (error) {
      if (error instanceof ApiJsonClientError) {
        setError(error.message || t(keys.errors.generic));
      } else {
        setError(t(keys.errors.generic));
      }
      return;
    }

    setSuccess(true);
  };

  return (
    <form
      key={formKey}
      className="mx-auto flex w-full max-w-[640px] flex-col gap-4"
      onSubmit={handleSubmit}
    >
      {error ? (
        <Alert message={error} helperText={t(keys.errors.recovery)} />
      ) : null}
      {success ? <SuccessNotice title={t(keys.common.updateSuccess)} /> : null}
      <FormField label={t(keys.common.name)} name="name">
        <TextInput
          name="name"
          defaultValue={defaultValues.name}
          placeholder={t(keys.placeholders.name)}
        />
      </FormField>
      <div className="grid gap-4 lg:grid-cols-2">
        <FormField label={t(keys.common.email)} name="email">
          <TextInput
            name="email"
            type="email"
            defaultValue={defaultValues.email}
            placeholder={t(keys.placeholders.email)}
          />
        </FormField>
        <FormField label={t(keys.common.phone)} name="phone">
          <PhoneInputField
            name="phone"
            value={phone}
            onChange={setPhone}
            locale={locale}
            labels={getPhoneInputLabels(t)}
          />
        </FormField>
      </div>
      <Button
        type="submit"
        variant="primary"
        className="h-11 w-full rounded-lg px-4 text-sm font-semibold transition"
      >
        {t(keys.common.update)}
      </Button>
    </form>
  );
}
